import { useMemo, useState } from 'react';
import { formatMoney } from '@/lib/finance';
import { emptyLine, lineNet, type InvoiceLineDraft } from '@/lib/invoiceDoc';
import { inputClass } from '@/pages/dashboard/finance/components/FinanceUI';

export interface CatalogItem {
  id: number;
  name: string;
  price: number;
  description: string;
  kind: 'product' | 'service';
}

interface Props {
  lines: InvoiceLineDraft[];
  onChange: (lines: InvoiceLineDraft[]) => void;
  currency: string;
  catalog: CatalogItem[];
  catalogLoading: boolean;
}

// Bloc 4 — détail des biens et services (catalogue + saisie libre).
export default function LineItemsEditor({ lines, onChange, currency, catalog, catalogLoading }: Props) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<'product' | 'service'>('product');

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return catalog
      .filter((c) => c.kind === tab)
      .filter((c) => !term || c.name.toLowerCase().includes(term));
  }, [catalog, tab, search]);

  const update = (idx: number, patch: Partial<InvoiceLineDraft>) => {
    onChange(lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  const addFromCatalog = (item: CatalogItem) => {
    const line: InvoiceLineDraft = {
      description: item.name,
      quantity: 1,
      unit_price: Number(item.price) || 0,
      discount: 0,
      item_type: item.kind,
      product_id: item.id,
    };
    const existing = lines.filter((l) => l.description.trim() || l.unit_price > 0);
    onChange([...existing, line]);
  };

  return (
    <section className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
          <i className="ri-list-check-2 text-primary-500"></i>
          Bloc 4 · Biens ou services
        </h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPickerOpen((v) => !v)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-accent-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer"
          >
            <i className={pickerOpen ? 'ri-close-line' : 'ri-shopping-bag-3-line'}></i>
            {pickerOpen ? 'Fermer' : 'Produits & services'}
          </button>
          <button
            type="button"
            onClick={() => onChange([...lines, emptyLine()])}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-background-200/70 bg-background-50 text-foreground-700 text-xs font-medium whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-add-line"></i>Ligne libre
          </button>
        </div>
      </div>

      {pickerOpen && (
        <div className="mb-3 p-3 rounded-lg bg-background-100 border border-background-200/70">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
            <div className="flex items-center gap-1 bg-background-50 rounded-full p-1 w-fit">
              {(['product', 'service'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTab(t)}
                  className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${tab === t ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:text-foreground-800'}`}
                >
                  {t === 'product' ? 'Produits' : 'Services'}
                </button>
              ))}
            </div>
            <div className="relative flex-1 min-w-[180px]">
              <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`${inputClass} pl-8`}
                placeholder="Rechercher…"
              />
            </div>
          </div>

          {catalogLoading ? (
            <p className="text-xs text-foreground-500 py-3 text-center"><i className="ri-loader-4-line animate-spin"></i> Chargement du catalogue…</p>
          ) : filtered.length === 0 ? (
            <p className="text-xs text-foreground-500 py-3 text-center">
              Aucun élément trouvé. Ajoutez vos {tab === 'product' ? 'produits' : 'services'} depuis leur page dédiée.
            </p>
          ) : (
            <div className="max-h-52 overflow-y-auto divide-y divide-background-200/70">
              {filtered.map((item) => (
                <button
                  key={`${item.kind}-${item.id}`}
                  type="button"
                  onClick={() => addFromCatalog(item)}
                  className="w-full flex items-center gap-3 py-2 text-left hover:bg-background-50 rounded-md px-2 transition-colors cursor-pointer"
                >
                  <span className="w-7 h-7 rounded-md bg-background-50 border border-background-200/70 text-foreground-500 flex items-center justify-center flex-shrink-0">
                    <i className={item.kind === 'product' ? 'ri-shopping-bag-3-line text-xs' : 'ri-service-line text-xs'}></i>
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs font-medium text-foreground-900 truncate">{item.name}</span>
                    {item.description && <span className="block text-[11px] text-foreground-400 truncate">{item.description}</span>}
                  </span>
                  <span className="text-xs font-semibold text-foreground-800 whitespace-nowrap">{formatMoney(item.price, currency)}</span>
                  <i className="ri-add-circle-line text-accent-600"></i>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="hidden sm:grid grid-cols-[1fr_60px_90px_85px_90px_28px] gap-2 px-1 mb-1 text-[11px] font-semibold text-foreground-500">
        <span>Désignation</span>
        <span className="text-right">Qté</span>
        <span className="text-right">P.U. HT</span>
        <span className="text-right">Remise</span>
        <span className="text-right">Total HT</span>
        <span></span>
      </div>

      <div className="space-y-2">
        {lines.map((line, idx) => (
          <div key={idx} className="grid grid-cols-2 sm:grid-cols-[1fr_60px_90px_85px_90px_28px] gap-2 items-center">
            <input
              value={line.description}
              onChange={(e) => update(idx, { description: e.target.value })}
              className={`${inputClass} col-span-2 sm:col-span-1`}
              placeholder="Désignation du produit ou service"
            />
            <input
              type="number" min="0" step="1" value={line.quantity}
              onChange={(e) => update(idx, { quantity: parseFloat(e.target.value) || 0 })}
              className={`${inputClass} text-right`} placeholder="Qté"
            />
            <input
              type="number" min="0" step="0.01" value={line.unit_price}
              onChange={(e) => update(idx, { unit_price: parseFloat(e.target.value) || 0 })}
              className={`${inputClass} text-right`} placeholder="P.U."
            />
            <input
              type="number" min="0" step="0.01" value={line.discount}
              onChange={(e) => update(idx, { discount: parseFloat(e.target.value) || 0 })}
              className={`${inputClass} text-right`} placeholder="0"
            />
            <span className="text-xs font-semibold text-foreground-900 text-right whitespace-nowrap">{formatMoney(lineNet(line), currency)}</span>
            <button
              type="button"
              onClick={() => onChange(lines.length > 1 ? lines.filter((_, i) => i !== idx) : [emptyLine()])}
              className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer justify-self-end"
              title="Retirer la ligne"
            >
              <i className="ri-delete-bin-line text-sm"></i>
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}